import { useLocalizedNavigate } from '../../../hooks/useLocalizedNavigate';
import {
    Icon,
    PageLoader,
    TextField,
    TextInput,
    UserAvatar,
} from '../../common';
import { useParams } from 'react-router-dom';
import { COLOURS } from '../../../utils/consts';
import { useTranslation } from '../../../hooks/translations/TranslationProvider';
import { formatDistanceWithLocale } from '../../../utils/helpers';
import { useEventBoard } from './useEventBoard';
import { useAppSelector } from '../../../store/hooks';

import './eventBoard.css';

interface EventBoardProps {
    isEventCancelled: boolean;
}

const EventBoard: React.FC<EventBoardProps> = ({
    isEventCancelled = false,
}) => {
    const { eventId } = useParams();
    const navigate = useLocalizedNavigate();
    const { t } = useTranslation();
    const auth = useAppSelector(state => state.auth);

    const {
        error,
        boardMessages,
        isLoading,
        newMessage,
        onMessageType,
        onEnterPress,
        onWriteMessage,
    } = useEventBoard(eventId, isEventCancelled);

    const userClickHandler = (userId: string) => {
        navigate(`/users/${userId}`);
    };

    const onWriteMessageHandler = (
        event: React.KeyboardEvent<HTMLInputElement>
    ) => {
        if (!auth.authData) {
            return;
        }
        onWriteMessage(event);
    };

    if (isLoading) {
        return (
            <div className="event-board">
                <div className="event-board-message">
                    <PageLoader inContainer />
                </div>
            </div>
        );
    }

    return (
        <div className="event-board">
            <div className="event-board-messages">
                {boardMessages.length > 0 ? (
                    boardMessages.map(message => (
                        <div
                            key={message.id}
                            className="event-board-message-row"
                        >
                            <UserAvatar
                                imageSrc={message.user.avatarUrl}
                                size="m"
                                onClick={() =>
                                    userClickHandler(message.user.id)
                                }
                                averageRating={message.user.averageRating}
                            />
                            <div className="event-board-message">
                                <div>
                                    <TextField weight="bold" size="m">
                                        {message.user.username}
                                    </TextField>
                                    <TextField size="s">
                                        {`${formatDistanceWithLocale(
                                            message.date,
                                            auth.authData?.language!
                                        )} ${t('date.ago')}`}
                                    </TextField>
                                </div>
                                <TextField size="s">
                                    {message.message}
                                </TextField>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="event-board-message-row">
                        <TextField size="m">
                            {t('eventDetails.noMessages')}
                        </TextField>
                    </div>
                )}
                {isLoading && (
                    <div className="event-board-message">
                        <PageLoader />
                    </div>
                )}
            </div>
            {!isEventCancelled && (
                <div className="event-board-input">
                    <TextInput
                        value={newMessage}
                        name="newMessage"
                        onChange={onMessageType}
                        placeholder={t('eventDetails.boardInputPlaceholder')}
                        isDisabled={isLoading || !auth.authData}
                        error={error}
                        onKeyDown={onEnterPress}
                    />
                    <Icon
                        name="bx-send"
                        size="25px"
                        color={
                            isLoading || !auth.authData
                                ? '#c0c0c0'
                                : COLOURS.GREEN
                        }
                        cursor={auth.authData ? 'pointer' : 'default'}
                        title={t('eventDetails.sendMessage')}
                        onClick={onWriteMessageHandler}
                    />
                </div>
            )}
        </div>
    );
};

export default EventBoard;
